import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, faBook, faPlus, faPen, faTrash, faArrowUp, faArrowDown, 
  faCopy, faList, faToggleOn, faToggleOff, faSave, faCheckCircle, faTimesCircle 
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import matrizCurricularService from '../../../services/matrizCurricularService';
import produtoService from '../../../services/produtoService';

const MatrizCurricularEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [matriz, setMatriz] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [produtos, setProdutos] = useState([]);
  
  // Estado para modal de módulo
  const [showModuloModal, setShowModuloModal] = useState(false);
  const [editingModulo, setEditingModulo] = useState(null);
  const [moduloForm, setModuloForm] = useState({ nome: '', descricao: '' });

  // Estado para modal de disciplina
  const [showDisciplinaModal, setShowDisciplinaModal] = useState(false);
  const [currentModuloId, setCurrentModuloId] = useState(null);
  const [editingDisciplina, setEditingDisciplina] = useState(null);
  const [disciplinaForm, setDisciplinaForm] = useState({ 
    idProduto: '', 
    obrigatoria: true, 
    cargaHoraria: '' 
  });

  // Estado para expandir/colapsar módulos
  const [expandedModulos, setExpandedModulos] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Carregar matriz com módulos e disciplinas
      const matrizResponse = await matrizCurricularService.getByIdWithModulos(id);
      if (matrizResponse.success) {
        setMatriz(matrizResponse.data);
        setModulos(matrizResponse.data.modulos || []);
        
        // Expandir todos os módulos por padrão
        const expanded = {};
        (matrizResponse.data.modulos || []).forEach(mod => {
          expanded[mod.id] = true;
        });
        setExpandedModulos(expanded);
      }

      // Carregar produtos disponíveis (apenas livros - tipoProduto = 1)
      const produtosResponse = await produtoService.list({ page: 1, limit: 1000, tipoProduto: 1 });
      console.log('📦 Resposta da API de produtos:', produtosResponse);
      
      if (produtosResponse.success) {
        // Garantir que produtos seja sempre um array
        const produtosArray = Array.isArray(produtosResponse.data) 
          ? produtosResponse.data 
          : (produtosResponse.data?.items || produtosResponse.data?.produtos || []);
        setProdutos(produtosArray);
        console.log('✅ Produtos (livros) carregados:', produtosArray.length);
        if (produtosArray.length > 0) {
          console.log('Primeiro produto:', produtosArray[0]);
        }
      } else {
        console.error('❌ API retornou success: false');
      }
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      toast.error(err.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FUNÇÕES DE MÓDULO
  // ============================================

  const openModuloModal = (modulo = null) => {
    if (modulo) {
      setEditingModulo(modulo);
      setModuloForm({ nome: modulo.nome, descricao: modulo.descricao || '' });
    } else {
      setEditingModulo(null);
      setModuloForm({ nome: '', descricao: '' });
    }
    setShowModuloModal(true);
  };

  const closeModuloModal = () => {
    setShowModuloModal(false);
    setEditingModulo(null);
    setModuloForm({ nome: '', descricao: '' });
  };

  const handleSaveModulo = async () => {
    if (!moduloForm.nome.trim()) {
      toast.error('Nome do módulo é obrigatório');
      return;
    }

    try {
      if (editingModulo) {
        // Atualizar
        const response = await matrizCurricularService.updateModulo(editingModulo.id, moduloForm);
        if (response.success) {
          toast.success('Módulo atualizado com sucesso!');
          loadData();
          closeModuloModal();
        }
      } else {
        // Criar
        const response = await matrizCurricularService.createModulo(id, moduloForm);
        if (response.success) {
          toast.success('Módulo criado com sucesso!');
          loadData();
          closeModuloModal();
        }
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar módulo');
    }
  };

  const handleDeleteModulo = async (moduloId, moduloNome) => {
    if (!window.confirm(`Deseja realmente excluir o módulo "${moduloNome}"?\n\nTodas as disciplinas vinculadas também serão excluídas!`)) {
      return;
    }

    try {
      const response = await matrizCurricularService.deleteModulo(moduloId);
      if (response.success) {
        toast.success('Módulo excluído com sucesso!');
        loadData();
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao excluir módulo');
    }
  };

  const handleMoveModulo = async (moduloId, direction) => {
    try {
      const response = await matrizCurricularService.moveModulo(moduloId, direction);
      if (response.success) {
        toast.success('Módulo reordenado com sucesso!');
        loadData();
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao mover módulo');
    }
  };

  const handleDuplicateModulo = async (moduloId) => {
    try {
      const response = await matrizCurricularService.duplicateModulo(moduloId);
      if (response.success) {
        toast.success('Módulo duplicado com sucesso!');
        loadData();
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao duplicar módulo');
    }
  };

  const toggleModuloExpanded = (moduloId) => {
    setExpandedModulos(prev => ({
      ...prev,
      [moduloId]: !prev[moduloId]
    }));
  };

  // ============================================
  // FUNÇÕES DE DISCIPLINA
  // ============================================

  const openDisciplinaModal = (moduloId, disciplina = null) => {
    setCurrentModuloId(moduloId);
    
    if (disciplina) {
      setEditingDisciplina(disciplina);
      setDisciplinaForm({ 
        idProduto: disciplina.idProduto, 
        obrigatoria: disciplina.obrigatoria, 
        cargaHoraria: disciplina.cargaHoraria || '' 
      });
    } else {
      setEditingDisciplina(null);
      setDisciplinaForm({ idProduto: '', obrigatoria: true, cargaHoraria: '' });
    }
    
    setShowDisciplinaModal(true);
  };

  const closeDisciplinaModal = () => {
    setShowDisciplinaModal(false);
    setCurrentModuloId(null);
    setEditingDisciplina(null);
    setDisciplinaForm({ idProduto: '', obrigatoria: true, cargaHoraria: '' });
  };

  const handleSaveDisciplina = async () => {
    if (!disciplinaForm.idProduto) {
      toast.error('Produto/Disciplina é obrigatório');
      return;
    }

    try {
      if (editingDisciplina) {
        // Atualizar
        const response = await matrizCurricularService.updateDisciplina(editingDisciplina.id, disciplinaForm);
        if (response.success) {
          toast.success('Disciplina atualizada com sucesso!');
          loadData();
          closeDisciplinaModal();
        }
      } else {
        // Criar
        const response = await matrizCurricularService.createDisciplina(currentModuloId, disciplinaForm);
        if (response.success) {
          toast.success('Disciplina adicionada com sucesso!');
          loadData();
          closeDisciplinaModal();
        }
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar disciplina');
    }
  };

  const handleDeleteDisciplina = async (disciplinaId, disciplinaNome) => {
    if (!window.confirm(`Deseja realmente remover a disciplina "${disciplinaNome}"?`)) {
      return;
    }

    try {
      const response = await matrizCurricularService.deleteDisciplina(disciplinaId);
      if (response.success) {
        toast.success('Disciplina removida com sucesso!');
        loadData();
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao remover disciplina');
    }
  };

  const handleMoveDisciplina = async (disciplinaId, direction) => {
    try {
      const response = await matrizCurricularService.moveDisciplina(disciplinaId, direction);
      if (response.success) {
        toast.success('Disciplina reordenada com sucesso!');
        loadData();
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao mover disciplina');
    }
  };

  const handleToggleObrigatoria = async (disciplinaId) => {
    try {
      const response = await matrizCurricularService.toggleObrigatoria(disciplinaId);
      if (response.success) {
        toast.success('Status de obrigatoriedade alterado!');
        loadData();
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao alterar status');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Carregando matriz curricular...</div>
      </div>
    );
  }

  if (!matriz) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-600">Matriz não encontrada</div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/academico/matriz-curricular')}
              className="text-gray-600 hover:text-gray-800"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-xl" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faBook} className="text-blue-600" />
                {matriz.nome}
              </h1>
              <p className="text-gray-600 mt-1">
                {matriz.nomeCurso} - {matriz.ano} | Status: {matriz.ativa ? 'Ativa' : 'Inativa'}
              </p>
            </div>
            <Link
              to={`/academico/matriz-curricular/formulario/${id}`}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <FontAwesomeIcon icon={faPen} />
              Editar Dados Básicos
            </Link>
          </div>
        </div>

        {/* Botão Adicionar Módulo */}
        <div className="mb-6">
          <button
            onClick={() => openModuloModal()}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} />
            Adicionar Módulo
          </button>
        </div>

        {/* Lista de Módulos */}
        <div className="space-y-4">
          {modulos.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-6 text-center text-gray-600">
              Nenhum módulo cadastrado. Clique em "Adicionar Módulo" para começar.
            </div>
          ) : (
            modulos.map((modulo, index) => (
              <div key={modulo.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Header do Módulo */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => toggleModuloExpanded(modulo.id)}
                        className="hover:bg-blue-400 p-2 rounded transition-colors"
                      >
                        <FontAwesomeIcon 
                          icon={expandedModulos[modulo.id] ? faArrowDown : faArrowUp} 
                        />
                      </button>
                      <div>
                        <h3 className="text-lg font-bold">
                          Módulo {modulo.ordem}: {modulo.nome}
                        </h3>
                        {modulo.descricao && (
                          <p className="text-sm text-blue-100">{modulo.descricao}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Mover para cima */}
                      {index > 0 && (
                        <button
                          onClick={() => handleMoveModulo(modulo.id, 'up')}
                          className="hover:bg-blue-400 p-2 rounded transition-colors"
                          title="Mover para cima"
                        >
                          <FontAwesomeIcon icon={faArrowUp} />
                        </button>
                      )}
                      
                      {/* Mover para baixo */}
                      {index < modulos.length - 1 && (
                        <button
                          onClick={() => handleMoveModulo(modulo.id, 'down')}
                          className="hover:bg-blue-400 p-2 rounded transition-colors"
                          title="Mover para baixo"
                        >
                          <FontAwesomeIcon icon={faArrowDown} />
                        </button>
                      )}
                      
                      {/* Duplicar */}
                      <button
                        onClick={() => handleDuplicateModulo(modulo.id)}
                        className="hover:bg-blue-400 p-2 rounded transition-colors"
                        title="Duplicar módulo"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                      
                      {/* Editar */}
                      <button
                        onClick={() => openModuloModal(modulo)}
                        className="hover:bg-blue-400 p-2 rounded transition-colors"
                        title="Editar módulo"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      
                      {/* Excluir */}
                      <button
                        onClick={() => handleDeleteModulo(modulo.id, modulo.nome)}
                        className="hover:bg-red-500 p-2 rounded transition-colors"
                        title="Excluir módulo"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Disciplinas do Módulo */}
                {expandedModulos[modulo.id] && (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                        <FontAwesomeIcon icon={faList} />
                        Disciplinas ({modulo.disciplinas?.length || 0})
                      </h4>
                      <button
                        onClick={() => openDisciplinaModal(modulo.id)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm flex items-center gap-2 transition-colors"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        Adicionar Disciplina
                      </button>
                    </div>

                    {modulo.disciplinas && modulo.disciplinas.length > 0 ? (
                      <div className="space-y-2">
                        {modulo.disciplinas.map((disciplina, discIndex) => (
                          <div 
                            key={disciplina.id} 
                            className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 flex-1">
                                <span className="text-gray-600 font-medium">{discIndex + 1}.</span>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-800">
                                    {disciplina.nomeProduto}
                                  </div>
                                  {disciplina.cargaHoraria && (
                                    <div className="text-sm text-gray-600">
                                      Carga Horária: {disciplina.cargaHoraria}h
                                    </div>
                                  )}
                                </div>
                                
                                {/* Badge Obrigatória/Optativa */}
                                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                  disciplina.obrigatoria 
                                    ? 'bg-red-100 text-red-800' 
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {disciplina.obrigatoria ? 'Obrigatória' : 'Optativa'}
                                </span>
                              </div>
                              
                              <div className="flex items-center gap-2 ml-4">
                                {/* Mover para cima */}
                                {discIndex > 0 && (
                                  <button
                                    onClick={() => handleMoveDisciplina(disciplina.id, 'up')}
                                    className="text-gray-600 hover:text-blue-600 p-1"
                                    title="Mover para cima"
                                  >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                  </button>
                                )}
                                
                                {/* Mover para baixo */}
                                {discIndex < modulo.disciplinas.length - 1 && (
                                  <button
                                    onClick={() => handleMoveDisciplina(disciplina.id, 'down')}
                                    className="text-gray-600 hover:text-blue-600 p-1"
                                    title="Mover para baixo"
                                  >
                                    <FontAwesomeIcon icon={faArrowDown} />
                                  </button>
                                )}
                                
                                {/* Toggle Obrigatória */}
                                <button
                                  onClick={() => handleToggleObrigatoria(disciplina.id)}
                                  className={`p-1 ${disciplina.obrigatoria ? 'text-red-600 hover:text-red-800' : 'text-yellow-600 hover:text-yellow-800'}`}
                                  title={disciplina.obrigatoria ? 'Tornar optativa' : 'Tornar obrigatória'}
                                >
                                  <FontAwesomeIcon icon={disciplina.obrigatoria ? faToggleOn : faToggleOff} />
                                </button>
                                
                                {/* Editar */}
                                <button
                                  onClick={() => openDisciplinaModal(modulo.id, disciplina)}
                                  className="text-yellow-600 hover:text-yellow-800 p-1"
                                  title="Editar disciplina"
                                >
                                  <FontAwesomeIcon icon={faPen} />
                                </button>
                                
                                {/* Excluir */}
                                <button
                                  onClick={() => handleDeleteDisciplina(disciplina.id, disciplina.nomeProduto)}
                                  className="text-red-600 hover:text-red-800 p-1"
                                  title="Remover disciplina"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-4">
                        Nenhuma disciplina cadastrada neste módulo
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Modal de Módulo */}
        {showModuloModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-xl font-bold mb-4">
                  {editingModulo ? 'Editar Módulo' : 'Novo Módulo'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Módulo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={moduloForm.nome}
                      onChange={(e) => setModuloForm({ ...moduloForm, nome: e.target.value })}
                      placeholder="Ex: Módulo 1 - Fundamentos"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição
                    </label>
                    <textarea
                      value={moduloForm.descricao}
                      onChange={(e) => setModuloForm({ ...moduloForm, descricao: e.target.value })}
                      placeholder="Descrição opcional..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSaveModulo}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Salvar
                  </button>
                  <button
                    onClick={closeModuloModal}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Disciplina */}
        {showDisciplinaModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <h3 className="text-xl font-bold mb-4">
                  {editingDisciplina ? 'Editar Disciplina' : 'Adicionar Disciplina'}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Produto/Disciplina <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={disciplinaForm.idProduto}
                      onChange={(e) => setDisciplinaForm({ ...disciplinaForm, idProduto: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione um produto</option>
                      {produtos.map(produto => (
                        <option key={produto.idProduto} value={produto.idProduto}>
                          {produto.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Carga Horária (horas)
                    </label>
                    <input
                      type="number"
                      value={disciplinaForm.cargaHoraria}
                      onChange={(e) => setDisciplinaForm({ ...disciplinaForm, cargaHoraria: e.target.value })}
                      placeholder="Ex: 40"
                      min="0"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={disciplinaForm.obrigatoria}
                      onChange={(e) => setDisciplinaForm({ ...disciplinaForm, obrigatoria: e.target.checked })}
                      className="mr-2"
                    />
                    <label className="text-sm font-medium text-gray-700">
                      Disciplina obrigatória
                    </label>
                  </div>
                </div>
                
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleSaveDisciplina}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Salvar
                  </button>
                  <button
                    onClick={closeDisciplinaModal}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatrizCurricularEdit;
